/**
 * S5 · LEDGER — money, with provenance, inside a bracket.
 *
 *   realized · return % · n closed · hit rate · best · worst · BALANCE ·
 *   Sortino · max DD — every figure a server-side aggregate over the reader's
 *   bracket, never a number computed from a page
 *   the realized equity curve with the MARK overlay OFF by default
 *   the per-trade P&L strip for the bracket
 *   TRADES · by trade_id — virtualized, filterable, sortable
 *
 * Construction rules, stated as rules because this is where the old system
 * collapsed (C1/C2/C3):
 *
 *   * the primary curve is REALIZED-ONLY. `balance=` is equity INCLUDING
 *     unrealized on a full-balance position at 10–20× and moves tens of
 *     percent inside one open trade — a separate dashed overlay, never a
 *     line joined to the realized series, and never printed as "the balance";
 *   * an unknown P&L is a BOUNDED BAND, not a zero and not a hole;
 *   * an unterminated open never deletes data — it is clamped, and past the
 *     clamp it is a fault drawn as a marker;
 *   * Sortino only. Sharpe is printed as suppressed WITH its reason, because
 *     printing the omission tells a reader it was a decision (D16).
 *
 * COPY RULE (the 2026-07-29 sweep): the default reading of a figure is the
 * NUMBER, its ATTESTATION WORD and its LABEL. Formula, denominator, clock and
 * caveat are all still here — in a `title` or in HOW THESE ARE COMPUTED — and
 * nothing on the wire was dropped. Burying prose is required; deleting a fact
 * is forbidden.
 *
 * READING ORDER, top to bottom: the figures are the substance, the curve is
 * the illustration, and TRADES is LAST — a bounded scroller may never sit
 * above something the reader still needs.
 *
 * COUPLING: the trade row is fixed-height by contract. `estimateSize` below
 * and `--trade-h` in tokens.css move together, or the virtualizer mis-measures
 * and the scroll drifts.
 */

import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { Fragment, useCallback, useMemo, useState, type ReactNode } from "react";

import { api, type OrderRow, type SummaryMoney } from "../lib/api";
import { BracketBar } from "../lib/BracketBar";
import { refetchMsFor, scopeParam, useRange } from "../lib/bracket";
import { Money, money, num, signClass, Pairing, CloseEvidence } from "../lib/money";
import { navigate, useWideViewport } from "../lib/route";
import { age, clock, stamp } from "../lib/time";
import { VirtualList } from "../lib/VirtualList";
import { EquityChart, PnlBars } from "./EquityChart";

/** The money block, reconstructed from a `/ledger` payload that predates
 *  `/summary` — a node mid-deploy, or a summary read that failed while the
 *  ledger read succeeded.
 *
 *  Every figure the old payload cannot supply comes back NULL, so it renders
 *  as `—` rather than as a plausible zero. A hit rate of `0/13` on a ledger
 *  that never counted wins is a lie; `—` is a hole, and a hole is allowed. */
function legacyMoney(l: NonNullable<ReturnType<typeof useLedger>["data"]>): SummaryMoney {
  const d = l.denominators;
  return {
    realized: l.realized,
    return_pct: null,
    // attested + estimated + pending are exactly the closes; `out_of_scope` is
    // the open set and is deliberately not in this sum.
    n_closed: d.included + d.excluded_estimated + d.excluded_unresolved,
    n_attested: d.included,
    n_estimated: d.excluded_estimated,
    n_pending: d.excluded_unresolved,
    wins: 0,
    losses: 0,
    flats: 0,
    win_rate: null,
    best_pct: null,
    worst_pct: null,
    median_hold_s: null,
    n_open_at_end: l.n_open,
    balance: null,
    balance_mark: null,
    sortino: l.sortino,
    sharpe: null,
    sharpe_suppressed: "sqrt_n",
    sharpe_suppressed_reason: l.sharpe_suppressed_reason,
    max_drawdown_pct: l.max_drawdown_pct,
    series_capped: d.lower_bound,
    denominators: d,
  };
}

/** Typing seam for `legacyMoney` — the ledger payload's shape is declared in
 *  `lib/api` and is not restated here. */
function useLedger(p: { since: string | null; until: string | null; scope_label: string }, refetchInterval: number | false) {
  return useQuery({
    queryKey: ["ledger", p.since, p.until],
    queryFn: () => api.ledger(p),
    refetchInterval,
  });
}

/* ------------------------------------------------------------- primitives */

/** A labelled figure. The key is the label, the value is the datum; the
 *  literal space between them is deliberate, so a copy-paste of the surface
 *  reads as a sentence rather than as `Sortino9.33`. */
function Fig({ k, v, valueClass = "mono num", title }: { k: string; v: ReactNode; valueClass?: string; title?: string }) {
  return (
    <div className="fig" title={title}>
      <span className="fig__k">{k}</span>{" "}
      <span className={`fig__v ${valueClass}`}>{v}</span>
    </div>
  );
}

/** ONE summary datum, scanned left to right: NUMBER · attestation word ·
 *  label. The `title` carries the formula, the denominator and the clock —
 *  which is where all of that now lives, and the only place it lives. */
function SumFig({
  v,
  attn,
  k,
  title,
  valueClass = "mono num",
}: {
  v: ReactNode;
  attn?: ReactNode;
  k: string;
  title: string;
  valueClass?: string;
}) {
  return (
    <span className="sumfig" title={title}>
      <span className={`sumfig__v ${valueClass}`}>{v}</span>
      {attn !== undefined && attn !== null && <span className="sumfig__attn">{attn}</span>}
      <span className="sumfig__k">{k}</span>
    </span>
  );
}

/** An account level is not a signed P&L: it never carries `+`. */
function level(v: number | null | undefined): string {
  if (v === null || v === undefined || !Number.isFinite(v)) return "—";
  return `$${v.toFixed(2)}`;
}

function pct(v: number | null | undefined): string {
  return v === null || v === undefined || !Number.isFinite(v) ? "—" : `${v > 0 ? "+" : ""}${v.toFixed(2)}%`;
}

/** `MM-DD HH:MM:SS`, both halves through the shared tz plumbing so the clock
 *  toggle governs the date as well as the time. A bare `14:10:34` on a ledger
 *  that spans three months is unreadable. */
function whenCell(iso: string | null | undefined): string {
  const s = stamp(iso);
  if (s === "—" || s === "?") return s;
  return `${s.slice(5, 10)} ${clock(iso)}`;
}

function duration(row: OrderRow): string {
  if (!row.close_ts) return "open";
  if (!row.open_ts) return "—";
  return age((Date.parse(row.close_ts) - Date.parse(row.open_ts)) / 1000);
}

/* ---------------------------------------------------------------- filters */

type SortChoice = "open_ts:desc" | "open_ts:asc" | "pnl_usd:desc" | "pnl_usd:asc";
type Outcome = "" | "win" | "loss" | "flat" | "open" | "unknown";

const SORT_LABEL: Record<SortChoice, string> = {
  "open_ts:desc": "newest first",
  "open_ts:asc": "oldest first",
  "pnl_usd:desc": "P&L high → low",
  "pnl_usd:asc": "P&L low → high",
};

export function Equity() {
  const range = useRange();
  const refetchInterval = refetchMsFor(range);
  const bracketArgs = { since: range.since, until: range.until, scope_label: scopeParam(range) };

  const summary = useQuery({
    queryKey: ["summary", "ledger", range.since, range.until],
    queryFn: () => api.summary(bracketArgs),
    refetchInterval,
  });
  const ledger = useLedger(bracketArgs, refetchInterval);
  const equity = useQuery({
    queryKey: ["equity", range.since, range.until],
    queryFn: () => api.equity(bracketArgs),
    refetchInterval,
  });

  // OFF BY DEFAULT, always. The overlay is opt-in per session and never
  // persisted, so a reader never inherits a mark-to-market curve unasked.
  const [showMark, setShowMark] = useState(false);
  // Layout only. The RENDERER does not fork: one TradeRow, one row height per
  // breakpoint, and the height below must equal `--trade-h` in the stylesheet.
  const wide = useWideViewport(1200);

  const [outcome, setOutcome] = useState<Outcome>("");
  const [instrument, setInstrument] = useState("");
  const [direction, setDirection] = useState("");
  const [sortChoice, setSortChoice] = useState<SortChoice>("open_ts:desc");
  const [sort, dir] = sortChoice.split(":") as ["open_ts" | "pnl_usd", "desc" | "asc"];

  const trades = useInfiniteQuery({
    queryKey: ["trades", range.since, range.until, outcome, instrument, direction, sort, dir],
    initialPageParam: undefined as string | undefined,
    queryFn: ({ pageParam }) =>
      api.trades({
        ...bracketArgs,
        limit: 50,
        cursor: pageParam,
        outcome: outcome || undefined,
        instrument: instrument || undefined,
        direction: direction || undefined,
        sort,
        dir,
      }),
    getNextPageParam: (last) => last.meta.next_cursor ?? undefined,
  });
  const rows: OrderRow[] = useMemo(() => trades.data?.pages.flatMap((p) => p.rows) ?? [], [trades.data]);
  const onEnd = useCallback(() => {
    if (trades.hasNextPage && !trades.isFetchingNextPage) void trades.fetchNextPage();
  }, [trades]);

  const l = ledger.data;
  // /summary is the figure source; /ledger carries the same block (API §3) and
  // stands in when the summary read is still in flight.
  const m: SummaryMoney | undefined = summary.data?.money ?? l?.money ?? (l ? legacyMoney(l) : undefined);
  const label = summary.data?.window.label ?? range.label;
  const excludedNullSort = trades.data?.pages[0]?.meta.excluded_null_sort_key;

  return (
    <section className="surface surface--wide ledger">
      <header className="surface__head">
        <h1 className="surface__title">Ledger</h1>
        {m && (
          <div className="surface__meta mono">
            {m.n_attested} attested · {m.n_open_at_end} open
          </div>
        )}
      </header>

      <BracketBar />

      <div className="surface__body">
        {summary.isLoading && !m && <div className="stub">…</div>}
        {/* Only when there is no money block at all. A failed /summary read
            with a good /ledger read still has figures to show, and showing
            an error over them would hide facts the console holds. */}
        {summary.error && !m && <div className="stub stub--error">the money aggregate is unavailable</div>}

        {m && (
          <div className="summary">
            <span className="summary__label">{label}</span>
            <div className="summary__figs">
              <SumFig
                v={<Money a={m.realized} big />}
                k="realized"
                valueClass=""
                title={`sum of pnl_usd over closes attested by the venue, bracketed on close_ts · n=${m.n_attested} of ${m.n_closed} closed · ${m.n_estimated} estimated and ${m.n_pending} pending are excluded, never folded in`}
              />
              <SumFig
                v={<Money a={m.return_pct} terse />}
                k="return"
                valueClass=""
                title="modified Dietz over the bracket: realized ÷ (account level at the start + each capital flow weighted by the fraction of the bracket it was present for). A deposit is never read as profit."
              />
              <SumFig
                v={m.n_closed}
                k="closed"
                title={`closes whose close_ts falls in the bracket (venue clock) · ${m.n_attested} attested · ${m.n_estimated} estimated · ${m.n_pending} awaiting backfill`}
              />
              <SumFig
                v={m.win_rate === null ? "—" : `${Math.round(m.win_rate * 100)}%`}
                // The denominator is part of the figure, not a footnote: a
                // rate whose n the reader cannot see is a rate they cannot
                // weigh. Absent when there is no rate to qualify.
                attn={m.win_rate === null ? undefined : `${m.wins}/${m.n_attested}`}
                k="hit rate"
                title={`wins ÷ attested closes. ${m.wins} up · ${m.losses} down · ${m.flats} flat. Estimated and pending closes are not in the denominator; with no attested close there is no rate.`}
              />
              <SumFig
                v={pct(m.best_pct)}
                valueClass={`mono num ${signClass(m.best_pct)}`}
                k="best"
                title="largest pnl_pct on a single close in the bracket"
              />
              <SumFig
                v={pct(m.worst_pct)}
                valueClass={`mono num ${signClass(m.worst_pct)}`}
                k="worst"
                title="smallest pnl_pct on a single close in the bracket"
              />
              <SumFig
                v={level(m.balance?.value)}
                attn={m.balance ? m.balance.src : undefined}
                k="balance"
                title={
                  m.balance
                    ? `account level from the newest daily snapshot before the end of the bracket · read ${stamp(m.balance.ts)} · ${age(m.balance.read_age_s)} old. This is the attested level; it is not realized P&L and it is not the mark.`
                    : "no daily balance snapshot in this tenant"
                }
              />
              {m.balance && m.balance.read_age_s > 172800 && (
                <span className="chip chip--down" title="the daily snapshot loop has not written in over two days">
                  balance {age(m.balance.read_age_s)} old
                </span>
              )}
              <SumFig
                v={num(m.sortino)}
                k="Sortino"
                title={`downside-deviation ratio over the bracket's attested pnl_pct series, oldest to newest${m.series_capped ? " · series capped — this is a lower bound" : ""}`}
              />
              <SumFig
                v={`${num(m.max_drawdown_pct)}%`}
                k="max DD"
                title="deepest peak-to-trough fall of the compounded attested series in the bracket"
              />
              <SumFig
                v="suppressed"
                attn="√N"
                k="Sharpe"
                valueClass="dim"
                title={m.sharpe_suppressed_reason}
              />
              <SumFig
                v={m.median_hold_s === null ? "—" : age(m.median_hold_s)}
                k="median hold"
                title="median hold_seconds over attested closes in the bracket"
              />
            </div>

            {/* Everything the default reading no longer prints. Nothing here
                is paraphrased: the wire's own sentences are quoted. */}
            <details className="summary__more">
              <summary>how these are computed</summary>
              <div className="summary__detail">
                <dl className="kvs">
                  <dt className="kv__k">scope</dt>
                  <dd className="kv__v mono">{label}</dd>
                  {summary.data &&
                    Object.entries(summary.data.clocks).map(([k, v]) => (
                      <Fragment key={k}>
                        <dt className="kv__k">{k} clock</dt>
                        <dd className="kv__v mono">{v}</dd>
                      </Fragment>
                    ))}
                  <dt className="kv__k">lower bound</dt>
                  <dd className="kv__v mono">{String(m.denominators.lower_bound)}</dd>
                  {m.balance_mark && (
                    <>
                      <dt className="kv__k">mark (incl. unrealized)</dt>
                      <dd className="kv__v mono num">{level(m.balance_mark.value)}</dd>
                    </>
                  )}
                </dl>

                {/* The denominator ledger, unchanged and unabridged — moved
                    one click down, not cut. Every aggregate above states what
                    it excluded; an aggregate that cannot is not shipped. */}
                <div className="denoms">
                  <div className="figs">
                    <div className="fig">
                      <span className="fig__v mono num">n={m.denominators.included}</span>{" "}
                      <span className="fig__k">attested</span>
                    </div>
                    <div className="fig">
                      <span className="fig__v mono num">{m.denominators.excluded_unresolved}</span>{" "}
                      <span className="fig__k">pending backfill</span>
                    </div>
                    <div className="fig">
                      <span className="fig__v mono num">{m.denominators.excluded_estimated}</span>{" "}
                      <span className="fig__k">estimated</span>
                    </div>
                    <div className="fig">
                      <span className="fig__v mono num">{m.n_open_at_end}</span>{" "}
                      <span className="fig__k">open at the end of the bracket</span>
                    </div>
                  </div>
                  {l?.note && <p className="note">{l.note}</p>}
                </div>

                <p className="note">{m.sharpe_suppressed_reason}</p>
                {equity.data && <p className="note">{equity.data.mark_overlay.why}</p>}
                {equity.data && <p className="note">{equity.data.band.basis}</p>}
              </div>
            </details>
          </div>
        )}

        <div className="band">
          <label
            className="toggle-inline"
            title={equity.data?.mark_overlay.why ?? "equity including unrealized on a full-balance position at 10–20×"}
          >
            <input type="checkbox" checked={showMark} onChange={(e) => setShowMark(e.target.checked)} />
            <span>MARK overlay (equity incl. unrealized)</span>
          </label>
        </div>

        <EquityChart payload={equity.data} showMark={showMark} />

        <section className="band">
          <div className="band__title">PER-TRADE P&amp;L · oldest → newest</div>
          <PnlBars rows={rows} />
        </section>

        {/* LAST on the surface, on purpose: this is the one bounded scroller
            here, and nothing the reader needs may sit below it. */}
        <section className="band">
          <div className="band__title">TRADES · by trade_id</div>

          <div className="row-actions">
            <label>
              outcome{" "}
              <select value={outcome} onChange={(e) => setOutcome(e.target.value as Outcome)}>
                <option value="">all</option>
                <option value="win">win</option>
                <option value="loss">loss</option>
                <option value="flat">flat</option>
                <option value="open">open</option>
                <option value="unknown">unknown P&amp;L</option>
              </select>
            </label>
            <label>
              instrument{" "}
              <select value={instrument} onChange={(e) => setInstrument(e.target.value)}>
                <option value="">all</option>
                <option value="wti">wti</option>
                <option value="sp500">sp500</option>
              </select>
            </label>
            <label>
              direction{" "}
              <select value={direction} onChange={(e) => setDirection(e.target.value)}>
                <option value="">all</option>
                <option value="up">up</option>
                <option value="down">down</option>
              </select>
            </label>
            <label>
              sort{" "}
              <select value={sortChoice} onChange={(e) => setSortChoice(e.target.value as SortChoice)}>
                {(Object.keys(SORT_LABEL) as SortChoice[]).map((s) => (
                  <option key={s} value={s}>
                    {SORT_LABEL[s]}
                  </option>
                ))}
              </select>
            </label>
            {sort === "pnl_usd" && !!excludedNullSort && (
              <span
                className="chip chip--down"
                title="an order with no P&L has no place in a P&L ordering — these rows are excluded from this sort, not ranked as zero"
              >
                {excludedNullSort} without P&amp;L excluded
              </span>
            )}
          </div>

          <VirtualList
            rows={rows}
            ariaLabel="trades by trade_id"
            className="vlist vlist--trades"
            estimateSize={wide ? 48 : 76}
            rowKey={(r) => r.trade_id}
            onEndReached={onEnd}
            emptyLabel={
              trades.isLoading ? (
                <div className="stub">…</div>
              ) : (
                <div className="empty">
                  <div
                    className="empty__title"
                    title="an order appears here the moment a signal clears its cell's gate and the venue accepts the instruction"
                  >
                    no orders in {label}
                  </div>
                </div>
              )
            }
            renderRow={(r) => <TradeRow row={r} />}
            footer={
              <div className="feed__footer muted">
                {trades.isFetchingNextPage && <span>loading…</span>}
                {!trades.hasNextPage && rows.length > 0 && <span>end of range</span>}
              </div>
            }
          />
        </section>
      </div>
    </section>
  );
}

/** ONE row renderer, at every viewport. The ten cells are always emitted, in
 *  a fixed order, so the grid can make them true columns — a ledger is scanned
 *  down the P&L column, never across a wrapped flex line. */
function TradeRow({ row }: { row: OrderRow }) {
  return (
    <button className="trade" onClick={() => navigate({ name: "order", tradeId: row.trade_id })}>
      <span className="trade__when mono">{whenCell(row.open_ts)}</span>
      <span className="trade__instr">{row.instrument ?? "—"}</span>
      <span className="trade__dir">{(row.direction ?? "—").toUpperCase()}</span>
      <span className="trade__dur mono num">{duration(row)}</span>
      <span className="trade__pnl">
        <Money a={row.pnl} terse />
      </span>
      <span className={`trade__pct num mono ${signClass(row.pnl_pct)}`}>{money(row.pnl_pct, "pct")}</span>
      <span className="trade__pairing">
        <Pairing pairing={row.pairing} />
      </span>
      <span className="trade__evidence">
        {/* NOT `compact`. The trades list has no header row, so dropping the
            word leaves a bare `NONE` with nothing to attach it to — and
            "every number keeps its formula" covers this column too. The
            clipping that motivated compact is fixed in the track width
            instead (9 rem), with an ellipsis and a title past that. */}
        <CloseEvidence evidence={row.close_evidence} />
        {row.unterminated === 1 && (
          <span className="chip chip--down" title="no terminal row was ever observed for this open">
            unterminated
          </span>
        )}
      </span>
      <span className="trade__venue">{row.venue ?? "—"}</span>
      <span className="trade__id mono" title={row.trade_id}>
        …{row.trade_id.slice(-6)}
      </span>
    </button>
  );
}

/* ------------------------------------------------------------ /o/<trade_id> */

/** ORDER forensics — "did the venue accept my instruction" — rendered beside
 *  the POSITION record — "do I have exposure and is it protected". Every
 *  recurring incident in this system is a disagreement between the two, and
 *  fusing them into one action stream is exactly what makes that class hard
 *  to see (§2.4). */
export function OrderView({ tradeId }: { tradeId: string }) {
  const q = useQuery({ queryKey: ["order", tradeId], queryFn: () => api.order(tradeId) });
  if (q.isLoading) return <div className="stub">…</div>;
  if (q.error) return <div className="stub stub--error">no such order in this tenant</div>;
  const { order, actions } = q.data;
  return (
    <section className="surface">
      <header className="surface__head">
        <h1 className="surface__title">Order</h1>
        <div className="surface__meta mono" title={order.trade_id}>
          {order.trade_id}
        </div>
      </header>

      <div className="surface__body">
        <div className="figs">
          <Fig k="instrument" v={order.instrument ?? "—"} valueClass="mono" />
          <Fig k="direction" v={(order.direction ?? "—").toUpperCase()} valueClass="mono" />
          <Fig k="size" v={order.size ?? "—"} />
          <Fig k="venue" v={order.venue ?? "—"} valueClass="mono" />
          <Fig k="state" v={order.state} valueClass="mono" />
        </div>

        <div className="row-actions">
          <Pairing pairing={order.pairing} />
          {order.post_id && (
            <button className="linkish" onClick={() => navigate({ name: "case", postId: order.post_id, band: "order" })}>
              → its case
            </button>
          )}
        </div>

        <section className="band">
          <div className="band__title">POSITION</div>
          <div className="figs">
            <Fig k="deal_id" v={order.position?.deal_id ?? "—"} valueClass="mono" />
            <Fig k="state" v={order.position?.state ?? "—"} valueClass="mono" />
          </div>
          <div className="row-actions">
            <CloseEvidence evidence={order.position?.close_evidence} />
          </div>
          {order.position?.venue_reason && <p className="note">{order.position.venue_reason}</p>}
        </section>

        <section className="band">
          <div className="band__title">P&amp;L</div>
          <Money a={order.pnl} big />
        </section>

        <section className="band">
          <div className="band__title">ACTIONS · the literal journal rows</div>
          <VirtualList
            rows={actions ?? []}
            ariaLabel="order action rows"
            className="vlist vlist--actions"
            estimateSize={30}
            rowKey={(a: any, i) => `${a.seq}-${i}`}
            renderRow={(a: any) => (
              <div className="mono raw__row">
                <span className="chip">{a.action}</span> {clock(a.ts)} <span className="dim">{a.journal}</span>
              </div>
            )}
            emptyLabel={<div className="stub">no lifecycle rows</div>}
          />
        </section>
      </div>
    </section>
  );
}
